import { BaseApi } from './base';
import type { Card, CardActionPayload, CardDelivery } from './types';

interface CreateCardPayload {
  title: string;
  icon: string;
  description: string;
  actions: CardActionPayload[];
}

interface UpdateCardPayload {
  title: string;
  icon: string;
  description: string;
  actions: CardActionPayload[];
}

interface DeliverCardPayload {
  userIds: string[];
}

export class CardsApi extends BaseApi {
  /**
   * Get all manual card presets for the group
   */
  async getAll(): Promise<Card[]> {
    return await this._request<Card[]>('/cards', {
      method: 'GET',
    });
  }

  /**
   * Get a specific card by ID
   */
  async getById(id: string): Promise<Card> {
    return await this._request<Card>(`/card/${id}`, {
      method: 'GET',
    });
  }

  /**
   * Create a new manual card preset
   */
  async create(payload: CreateCardPayload): Promise<Card> {
    return await this._request<Card>('/card', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  /**
   * Update a manual card preset
   */
  async update(id: string, payload: UpdateCardPayload): Promise<Card> {
    return await this._request<Card>(`/card/${id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  }

  /**
   * Delete a manual card preset
   */
  async delete(id: string): Promise<{ message: string }> {
    return await this._request<{ message: string }>(`/card/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Manually deliver a card to one or more users
   */
  async deliver(cardId: string, userIds: string[]): Promise<CardDelivery[]> {
    return await this._request<CardDelivery[]>(`/card/${cardId}/deliver`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ userIds }),
    });
  }

  /**
   * Get card deliveries for a specific user
   */
  async getDeliveries(userId: string): Promise<CardDelivery[]> {
    return await this._request<CardDelivery[]>(`/cards/deliveries/${userId}`, {
      method: 'GET',
    });
  }
}

export type { CreateCardPayload, DeliverCardPayload, UpdateCardPayload };
